import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent } from '@ohif/ui-next';
import { ICON_STROKE_WIDTH } from '../../constants';
import RequiredLabel from '../../components/common/RequiredLabel';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import {
  buildSchedulePayload,
  getDefaultMaintenanceFormValues,
  isoToDatetimeLocal,
} from '../../utils/maintenanceMapper';
import machineMaintenanceService from '../../services/machineMaintenanceService';
import type { Machine } from '../../types/machine.types';
import type { MaintenanceFormValues, MaintenanceWindow } from '../../types/maintenance.types';
import './MachineManagement.css';

interface ScheduleMaintenanceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (window: MaintenanceWindow) => void;
  machines: Machine[];
  facilityName: string;
  editingWindow?: MaintenanceWindow | null;
}

const ScheduleMaintenanceDialog: React.FC<ScheduleMaintenanceDialogProps> = ({
  open,
  onClose,
  onSuccess,
  machines,
  facilityName,
  editingWindow = null,
}) => {
  const isEdit = Boolean(editingWindow);
  const [values, setValues] = useState<MaintenanceFormValues>(getDefaultMaintenanceFormValues());
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof MaintenanceFormValues, string>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setValues(getDefaultMaintenanceFormValues());
      setFieldErrors({});
      setSubmitError(null);
      setSubmitting(false);
      return;
    }

    if (editingWindow) {
      setValues({
        machineId: editingWindow.machineId != null ? String(editingWindow.machineId) : '',
        starts: isoToDatetimeLocal(editingWindow.scheduledStart),
        ends: isoToDatetimeLocal(editingWindow.scheduledEnd),
        reason: editingWindow.reason || '',
      });
    } else {
      setValues(getDefaultMaintenanceFormValues());
    }
    setFieldErrors({});
    setSubmitError(null);
  }, [open, editingWindow]);

  const machineOptions = useMemo(() => {
    const fromRegistry = machines.map(m => ({
      value: String(m.id),
      label: `${m.machineName}${m.modality ? ` (${m.modality})` : ''}`,
    }));

    if (
      editingWindow &&
      editingWindow.machineId == null &&
      !fromRegistry.some(
        o =>
          o.label === editingWindow.machineName ||
          o.label.startsWith(`${editingWindow.machineName} `)
      )
    ) {
      return [
        { value: `seed:${editingWindow.id}`, label: editingWindow.machineName },
        ...fromRegistry,
      ];
    }

    if (fromRegistry.length === 0) {
      return [];
    }

    return fromRegistry;
  }, [machines, editingWindow]);

  const setField = useCallback((field: keyof MaintenanceFormValues, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const errors: Partial<Record<keyof MaintenanceFormValues, string>> = {};
    if (!values.machineId && !editingWindow) {
      errors.machineId = 'Select a machine.';
    }
    if (!values.starts) {
      errors.starts = 'Start time is required.';
    }
    if (!values.ends) {
      errors.ends = 'End time is required.';
    }
    if (values.starts && values.ends) {
      const start = new Date(values.starts).getTime();
      const end = new Date(values.ends).getTime();
      if (!Number.isNaN(start) && !Number.isNaN(end) && end <= start) {
        errors.ends = 'End time must be after start time.';
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [values, editingWindow]);

  const resolveMachine = useCallback(() => {
    if (values.machineId && !values.machineId.startsWith('seed:')) {
      const found = machines.find(m => String(m.id) === values.machineId);
      if (found) {
        return {
          id: found.id,
          machineName: found.machineName,
          modality: found.modality,
          location: found.location,
        };
      }
    }
    if (editingWindow) {
      return {
        id: editingWindow.machineId ?? 0,
        machineName: editingWindow.machineName,
        modality: editingWindow.modality,
        location: editingWindow.location,
      };
    }
    return null;
  }, [values.machineId, machines, editingWindow]);

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const machine = resolveMachine();
    if (!machine) {
      setFieldErrors(prev => ({ ...prev, machineId: 'Select a machine.' }));
      return;
    }

    const payload = buildSchedulePayload(values, machine, facilityName);
    if (!payload) {
      setSubmitError('Invalid schedule details.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const result =
        isEdit && editingWindow
          ? await machineMaintenanceService.updateWindow(editingWindow.id, payload)
          : await machineMaintenanceService.scheduleWindow(payload);

      if (result.success && result.data) {
        onSuccess(result.data);
        onClose();
      } else {
        setSubmitError(result.error || 'Failed to save maintenance window.');
      }
    } catch {
      setSubmitError('Failed to save maintenance window.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && onClose()}
    >
      <DialogContent
        className="machine-register-dialog-content mm-schedule-dialog"
        showCloseButton={false}
      >
        <header className="machine-register-dialog-header">
          <div className="machine-register-dialog-header-text">
            <h2 className="machine-register-dialog-title">
              {isEdit ? 'Edit maintenance' : 'Schedule maintenance'}
            </h2>
            <p className="machine-register-dialog-subtitle">
              MWL requests are blocked for the selected machine while the window is active.
            </p>
          </div>
          <button
            type="button"
            className="machine-register-dialog-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X
              size={20}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
        </header>

        <div className="machine-register-dialog-body machine-form-body">
          <div className="machine-form-field">
            <FormSelectVirtualized
              label="Machine"
              required
              name="maintenanceMachine"
              options={machineOptions}
              value={values.machineId}
              onChange={e => setField('machineId', e.target.value)}
              error={fieldErrors.machineId}
              placeholder="Select a machine"
            />
          </div>

          <div className="machine-form-row">
            <div className="machine-form-field">
              <RequiredLabel
                label="Starts"
                required
                htmlFor="mm-starts"
              />
              <input
                id="mm-starts"
                type="datetime-local"
                className="mm-datetime-input"
                value={values.starts}
                onChange={e => setField('starts', e.target.value)}
                aria-invalid={Boolean(fieldErrors.starts)}
              />
              {fieldErrors.starts ? (
                <span className="mm-field-error">{fieldErrors.starts}</span>
              ) : null}
            </div>
            <div className="machine-form-field">
              <RequiredLabel
                label="Ends"
                required
                htmlFor="mm-ends"
              />
              <input
                id="mm-ends"
                type="datetime-local"
                className="mm-datetime-input"
                value={values.ends}
                onChange={e => setField('ends', e.target.value)}
                aria-invalid={Boolean(fieldErrors.ends)}
              />
              {fieldErrors.ends ? <span className="mm-field-error">{fieldErrors.ends}</span> : null}
            </div>
          </div>

          <div className="machine-form-field">
            <label
              className="create-user-dialog-label"
              htmlFor="mm-reason"
            >
              Reason / remarks
            </label>
            <textarea
              id="mm-reason"
              className="mm-reason-textarea"
              rows={4}
              placeholder="e.g. Quarterly preventive maintenance"
              value={values.reason}
              onChange={e => setField('reason', e.target.value)}
            />
          </div>

          {submitError ? <p className="machine-form-submit-error">{submitError}</p> : null}
        </div>

        <footer className="machine-register-dialog-footer">
          <button
            type="button"
            className="machine-dialog-btn machine-dialog-btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="machine-dialog-btn machine-dialog-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? isEdit
                ? 'Saving…'
                : 'Scheduling…'
              : isEdit
                ? 'Save changes'
                : 'Schedule maintenance'}
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleMaintenanceDialog;
